/* gwm-product.js
   - Mobile: scroll-snap slider synced with dots + thumbnails
   - Lightbox: click any image (desktop or mobile) to open fullscreen
   - Accordion
   - Variants: find match → update price/ATC + swap hero image + update selected labels
   - Material cards: click to open modal with full image + description
*/

(function () {
  'use strict';

  function qs(el, sel)  { return el.querySelector(sel); }
  function qsa(el, sel) { return Array.from(el.querySelectorAll(sel)); }
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const cleanupMap = new WeakMap();

  /* ─────────────────────────────────────────────
     LIGHTBOX (one shared instance for whole page)
  ───────────────────────────────────────────── */
  let lb = null;

  function buildLightbox() {
    if (lb) return lb;

    const el = document.createElement('div');
    el.className = 'gwm-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.innerHTML = `
      <button class="gwm-lightbox__close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <button class="gwm-lightbox__prev" aria-label="Previous">&#8592;</button>
      <img class="gwm-lightbox__img" src="" alt="">
      <button class="gwm-lightbox__next" aria-label="Next">&#8594;</button>
      <div class="gwm-lightbox__counter"></div>
    `;
    document.body.appendChild(el);

    let srcs = [];
    let current = 0;

    const img     = qs(el, '.gwm-lightbox__img');
    const counter = qs(el, '.gwm-lightbox__counter');

    const show = (idx) => {
      current = clamp(idx, 0, srcs.length - 1);
      img.src = srcs[current];
      counter.textContent = srcs.length > 1 ? `${current + 1} / ${srcs.length}` : '';
    };

    const open = (images, startIdx) => {
      srcs = images;
      show(startIdx);
      el.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      el.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    qs(el, '.gwm-lightbox__close').addEventListener('click', close);
    qs(el, '.gwm-lightbox__prev').addEventListener('click', () => show(current - 1));
    qs(el, '.gwm-lightbox__next').addEventListener('click', () => show(current + 1));

    el.addEventListener('click', (e) => { if (e.target === el) close(); });

    document.addEventListener('keydown', (e) => {
      if (!el.classList.contains('is-open')) return;
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowLeft')  show(current - 1);
      if (e.key === 'ArrowRight') show(current + 1);
    });

    let lbTx = 0;
    img.addEventListener('touchstart', (e) => { lbTx = e.touches[0].clientX; }, { passive: true });
    img.addEventListener('touchend',   (e) => {
      const diff = lbTx - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 40) return;
      diff > 0 ? show(current + 1) : show(current - 1);
    }, { passive: true });

    lb = { open };
    return lb;
  }

  /* ─────────────────────────────────────────────
     MATERIAL MODAL (one shared instance for whole page)
  ───────────────────────────────────────────── */
  let matModal = null;

  function buildMatModal() {
    if (matModal) return matModal;

    const el = document.createElement('div');
    el.className = 'gwm-mat-modal';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.innerHTML = `
      <div class="gwm-mat-modal__inner">
        <button class="gwm-mat-modal__close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <img class="gwm-mat-modal__img" src="" alt="">
        <h3 class="gwm-mat-modal__name"></h3>
        <p class="gwm-mat-modal__desc"></p>
        <div class="gwm-mat-modal__popup"></div>
      </div>
    `;
    document.body.appendChild(el);

    const close = () => {
      el.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    qs(el, '.gwm-mat-modal__close').addEventListener('click', close);
    el.addEventListener('click', (e) => { if (e.target === el) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && el.classList.contains('is-open')) close();
    });

    const open = (data) => {
      qs(el, '.gwm-mat-modal__img').src            = data.image || '';
      qs(el, '.gwm-mat-modal__img').alt            = data.name  || '';
      qs(el, '.gwm-mat-modal__name').textContent   = data.name  || '';
      qs(el, '.gwm-mat-modal__desc').textContent   = data.desc  || '';
      qs(el, '.gwm-mat-modal__popup').innerHTML    = data.popup || '';
      el.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    matModal = { open };
    return matModal;
  }

  function initAll() {
    buildLightbox();
    buildMatModal();

    document.querySelectorAll('.gwm-pdp').forEach((section) => {

      if (cleanupMap.has(section)) {
        cleanupMap.get(section)();
        cleanupMap.delete(section);
      }

      const cleanups = [];

      /* ─────────────────────────────
         Collect all image srcs for lightbox
      ───────────────────────────── */
      const allImgSrcs = qsa(section, '.gwm-stack .gwm-block img, [data-gwm-slide] img')
        .map(img => img.src)
        .filter((src, i, arr) => arr.indexOf(src) === i);

      qsa(section, '.gwm-stack .gwm-block').forEach((block, i) => {
        const onClick = () => lb.open(allImgSrcs, i);
        block.addEventListener('click', onClick);
        cleanups.push(() => block.removeEventListener('click', onClick));
      });

      /* ─────────────────────────────
         MOBILE SLIDER
      ───────────────────────────── */
      const trackEl  = qs(section, '[data-gwm-slides]');
      const dotsEl   = qs(section, '[data-gwm-dots]');
      const thumbsEl = qs(section, '[data-gwm-thumbs]');

      if (trackEl) {
        const slides = qsa(trackEl,  '[data-gwm-slide]');
        const dots   = dotsEl   ? qsa(dotsEl,   '[data-gwm-dot]')   : [];
        const thumbs = thumbsEl ? qsa(thumbsEl, '[data-gwm-thumb]') : [];

        const syncActive = () => {
          const idx = Math.round(trackEl.scrollLeft / trackEl.clientWidth);
          const s = String(clamp(idx, 0, slides.length - 1));
          dots.forEach(  el => el.classList.toggle('is-active', el.dataset.index === s));
          thumbs.forEach(el => el.classList.toggle('is-active', el.dataset.index === s));
          const activeThumb = thumbs.find(t => t.dataset.index === s);
          if (activeThumb) activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        };

        const goTo = (idx) => {
          const clamped = clamp(idx, 0, slides.length - 1);
          trackEl.scrollTo({ left: clamped * trackEl.clientWidth, behavior: 'smooth' });
        };

        trackEl.addEventListener('scroll', syncActive, { passive: true });
        cleanups.push(() => trackEl.removeEventListener('scroll', syncActive));

        dots.forEach(  d => d.addEventListener('click', () => goTo(parseInt(d.dataset.index, 10))));
        thumbs.forEach(t => t.addEventListener('click', () => goTo(parseInt(t.dataset.index, 10))));

        slides.forEach((slide, i) => {
          const onClick = () => lb.open(allImgSrcs, i);
          slide.addEventListener('click', onClick);
          cleanups.push(() => slide.removeEventListener('click', onClick));
        });
      }

      /* ─────────────────────────────
         ACCORDION
      ───────────────────────────── */
      qsa(section, '[data-gwm-acc]').forEach((acc) => {
        const toggle = qs(acc, '[data-gwm-acc-toggle]');
        const body   = qs(acc, '[data-gwm-acc-body]');
        if (!toggle || !body) return;

        const onClick = () => {
          const open = acc.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', String(open));
          open ? body.removeAttribute('hidden') : body.setAttribute('hidden', '');
        };

        toggle.addEventListener('click', onClick);
        cleanups.push(() => toggle.removeEventListener('click', onClick));
      });

      /* ─────────────────────────────
         MATERIAL CARDS
      ───────────────────────────── */
      qsa(section, '[data-gwm-mat-card]').forEach((card) => {
        const onClick = () => {
          matModal.open({
            image: card.dataset.image || '',
            name:  card.dataset.name  || '',
            desc:  card.dataset.desc  || '',
            popup: card.dataset.popup || '',
          });
        };
        card.addEventListener('click', onClick);
        cleanups.push(() => card.removeEventListener('click', onClick));
      });

      /* ─────────────────────────────
         VARIANTS
      ───────────────────────────── */
      const form = qs(section, '[data-gwm-form]');
      if (form) {
        const varScript = qs(form, '[data-gwm-variants]');
        let variants = [];
        try { variants = varScript ? JSON.parse(varScript.textContent) : []; } catch (_) {}

        const idInput  = qs(form, 'input[name="id"]');
        const priceEl  = qs(section, '[data-gwm-price]');
        const atcBtn   = qs(section, '[data-gwm-atc]');
        const atcText  = qs(section, '[data-gwm-atc-text]');
        const atcPrice = qs(section, '[data-gwm-atc-price]');

        const money = (cents) => {
          const n = cents / 100;
          return '$' + (n % 1 === 0 ? n.toFixed(0) + '.00' : n.toFixed(2));
        };

        const getOptions = () =>
          qsa(form, '[data-gwm-option]').filter(i => i.checked).map(i => i.value);

        const findVariant = () => {
          const vals = getOptions();
          if (!vals.length) return variants[0];
          return variants.find(v => vals.every((val, i) => v[`option${i + 1}`] === val)) || variants[0];
        };

        const stackFirstImg  = qs(section, '.gwm-stack .gwm-block:first-child img');
        const sliderFirstImg = qs(section, '[data-gwm-slide][data-index="0"] img');

        const swapHeroImage = (v) => {
          if (!v || !v.featured_image) return;
          const src = v.featured_image.src.replace(/(\.[a-z]+)(\?.*)?$/, '_2000x$1');
          if (stackFirstImg)  stackFirstImg.src  = src;
          if (sliderFirstImg) sliderFirstImg.src = src;
        };

        const updateSelectedLabels = () => {
          qsa(form, '[data-gwm-option]').forEach((input) => {
            if (!input.checked) return;
            const pos = input.closest('[data-option-position]')?.dataset.optionPosition;
            if (!pos) return;
            const labelEl = qs(section, `[data-gwm-option-selected="${pos}"]`);
            if (labelEl) labelEl.textContent = input.value;
          });
        };

        const apply = (v) => {
          if (!v) return;
          if (idInput) idInput.value = v.id;

          if (priceEl) {
            const mainEl    = qs(priceEl, '.gwm-price__main');
            const compareEl = qs(priceEl, '.gwm-price__compare');
            if (mainEl) mainEl.textContent = money(v.price);
            if (compareEl) {
              const hasCompare = v.compare_at_price && v.compare_at_price > v.price;
              compareEl.textContent   = hasCompare ? money(v.compare_at_price) : '';
              compareEl.style.display = hasCompare ? '' : 'none';
            }
          }

          if (atcBtn) {
            atcBtn.disabled = !v.available;
            if (atcText)  atcText.textContent  = v.available ? 'Add to Cart' : 'Sold Out';
            if (atcPrice) atcPrice.textContent = money(v.price);
          }

          swapHeroImage(v);
          updateSelectedLabels();
        };

        apply(findVariant());

        qsa(form, '[data-gwm-option]').forEach((input) => {
          const onChange = () => apply(findVariant());
          input.addEventListener('change', onChange);
          cleanups.push(() => input.removeEventListener('change', onChange));
        });
      }

      cleanupMap.set(section, () => cleanups.forEach(fn => fn()));
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

})();